import type { UseMutationOptions } from '@tanstack/react-query';

import useToastedMutation from '../../../../../ReactQuery/useToastedMutation';
import { post, remove } from '../../../../../redux/api';

type GenerateScimTokenData = {
  token: string;
};

async function generateScimToken(organizationId: number) {
  const response = await post<void, GenerateScimTokenData>(`/api/organizations/${organizationId}/scim/token`, undefined);
  return response.data;
}

async function revokeScimToken(organizationId: number) {
  await remove<void, void>(`/api/organizations/${organizationId}/scim/token`);
}

type GenerateScimTokenResponse = Awaited<ReturnType<typeof generateScimToken>>;

type GenerateScimTokenOptions = Omit<UseMutationOptions<GenerateScimTokenResponse, Error, void>, 'mutationFn'>;

type RevokeScimTokenOptions = Omit<UseMutationOptions<void, Error, void>, 'mutationFn'>;

/**
 * Mints (or rotates) the organization's SCIM bearer token. The plaintext token is only ever returned once, in the
 * mutation response — there is no endpoint to retrieve it again afterward.
 */
export function useGenerateScimToken(organizationId: number, options?: GenerateScimTokenOptions) {
  return useToastedMutation({
    mutationFn: () => generateScimToken(organizationId),
    errorToastMessage: 'Failed to generate SCIM token.',
    ...options,
  });
}

/**
 * Revokes the organization's SCIM bearer token, disabling any identity provider currently using it.
 */
export function useRevokeScimToken(organizationId: number, options?: RevokeScimTokenOptions) {
  return useToastedMutation({
    mutationFn: () => revokeScimToken(organizationId),
    successToastMessage: 'SCIM token revoked.',
    errorToastMessage: 'Failed to revoke SCIM token.',
    ...options,
  });
}
